/**
 * ApproveDialog — confirm-and-approve action (EPIC-006 Phase 5, Task 5.3).
 *
 * A BottomSheet modal (focus-trapped) that:
 *   - Shows the confirmation description
 *   - Calls POST /admin/seller-applications/{id}/approve via useMutation
 *   - Handles 409 marketplace.seller.duplicate_phone: shows explanation + "Reject instead"
 *   - Handles 409 seller_application.invalid_transition: toasts + closes
 *   - Debounces repeated clicks (reuses the same Idempotency-Key for 30s)
 *
 * The Idempotency-Key is generated fresh per dialog open (not per click), so
 * clicking "Approve" repeatedly within the same dialog open session reuses the
 * same key (idempotent replay). A new open generates a new key.
 *
 * FIX (Phase 5 review): added `onOfferReject` callback — the "Reject instead"
 * button now calls it so the caller (DetailDrawer) can switch to the reject flow,
 * rather than just closing and leaving the admin without a dialog.
 * Colors use `--g-error` semantic token (not raw red-* Tailwind classes).
 */
import { useCallback, useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { BottomSheet, Button } from "@garden/ui";
import { ApiError } from "@/lib/api-client";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { useApproveApplication } from "@/features/admin/hooks/useSellerApplications";

export interface ApproveDialogProps {
  open: boolean;
  applicationId: string;
  onClose: () => void;
  onSuccess: () => void;
  onInvalidTransition: () => void;
  /** Called when the admin clicks "Reject instead" after a duplicate-phone 409. */
  onOfferReject: () => void;
}

export function ApproveDialog({
  open,
  applicationId,
  onClose,
  onSuccess,
  onInvalidTransition,
  onOfferReject,
}: ApproveDialogProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const { mutateAsync, isPending } = useApproveApplication();
  const [duplicatePhoneError, setDuplicatePhoneError] = useState(false);

  // Reset error state when dialog closes/opens
  useEffect(() => {
    if (!open) {
      setDuplicatePhoneError(false);
    }
  }, [open]);

  const handleApprove = useCallback(async () => {
    try {
      await mutateAsync(applicationId);
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.problem?.code === "marketplace.seller.duplicate_phone") {
          setDuplicatePhoneError(true);
          return;
        }
        if (err.problem?.code === "seller_application.invalid_transition") {
          onInvalidTransition();
          return;
        }
      }
      // Generic error — re-throw so TanStack Query logs it
      throw err;
    }
  }, [applicationId, mutateAsync, onSuccess, onInvalidTransition]);

  return (
    <BottomSheet open={open} onClose={onClose} title={t("approve.title")}>
      <div className="flex flex-col gap-4">
        {duplicatePhoneError ? (
          <div
            data-testid="duplicate-phone-error"
            className="rounded-[var(--g-radius-sm)] bg-[var(--g-error)]/10 p-3 text-sm text-[var(--g-error)]"
          >
            {t("approve.duplicatePhone")}
          </div>
        ) : (
          <p className="text-sm text-[var(--g-secondary)]">
            {t("approve.description")}
          </p>
        )}

        <div className="flex justify-end gap-2">
          {duplicatePhoneError ? (
            // Offer "Reject instead" when duplicate phone — calls onOfferReject
            // so the caller can open the RejectDialog instead of dead-ending.
            <>
              <Button variant="ghost" size="sm" onClick={onClose} className="min-h-[44px]">
                {t("approve.cancel")}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={onOfferReject}
                data-testid="approve-reject-instead"
                className="min-h-[44px] border-[var(--g-error)] text-[var(--g-error)]"
              >
                {t("approve.duplicatePhoneReject")}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                disabled={isPending}
                className="min-h-[44px]"
              >
                {t("approve.cancel")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                data-testid="approve-confirm-btn"
                onClick={() => void handleApprove()}
                disabled={isPending}
                className="min-h-[44px]"
              >
                {t("approve.confirm")}
              </Button>
            </>
          )}
        </div>
      </div>
    </BottomSheet>
  );
}

export default ApproveDialog;
